import {
  Alert,
  Button,
  Card,
  Checkbox,
  Divider,
  Form,
  Input,
  Space,
  Typography,
} from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDermatoWizard, useRegisterStepSave } from '../DermatoWizardContext';
import { clearWizardDraft, readWizardDraft, writeWizardDraft } from '../../../../utils/wizardDraft';
import {
  CHRONIC_SKIN_CONDITION_OPTIONS,
  ChoiceField,
  DetailTextInput,
  GENETIC_MUTATION_OPTIONS,
  HIGH_UV_ZONE_OPTIONS,
  MultiCheckWithOtherField,
  OTHER_CANCER_OPTION_VALUE,
  OTHER_CANCER_TYPE_OPTIONS,
  OTHER_OPTION_VALUE,
  OTHER_TREATMENT_OPTION_VALUE,
  PHOTOSENSITIZING_MEDICATION_OPTIONS,
  PREVIOUS_SKIN_CANCER_TYPE_OPTIONS,
  SELF_EXAM_OPTIONS,
  SKIN_CANCER_TREATMENT_OPTIONS,
  SKIN_CHANGES_WITH_OTHER_OPTIONS,
  SKIN_TYPE_OPTIONS,
  SPF_OPTIONS,
  SUNBURN_EPISODE_OPTIONS,
  SUN_EXPOSURE_OPTIONS,
  SectionHint,
  TANNING_PRODUCT_OPTIONS,
  TANNING_SALON_FREQUENCY_OPTIONS,
  TOXIC_CHEMICAL_OPTIONS,
  YesNoChoiceWithOtherField,
  YesNoDetailField,
  YesNoField,
  YesNoOptionsWithOtherField,
  YesNoUnknownField,
  type YesNo,
  type YesNoUnknown,
} from './anamnesisFields';
import './Step2Anamnesis.css';

const { Paragraph, Text } = Typography;

type SectionKey = 'medical' | 'family' | 'uv' | 'skin' | 'lifestyle' | 'notes' | 'consent';

interface AnamnesisValues {
  medical: Record<string, unknown>;
  family: Record<string, unknown>;
  uv: Record<string, unknown>;
  skin: Record<string, unknown>;
  lifestyle: Record<string, unknown>;
  skinType?: string;
  notes: string;
  consent: {
    personalDataProcessing: boolean;
    registryInclusion: boolean;
    futureContact: boolean;
  };
}

const emptyValues = (): AnamnesisValues => ({
  medical: {},
  family: {},
  uv: {},
  skin: {},
  lifestyle: {},
  skinType: undefined,
  notes: '',
  consent: {
    personalDataProcessing: false,
    registryInclusion: false,
    futureContact: false,
  },
});

const asRecord = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};

const asString = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));

const asStringArray = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];

const asYesNo = (v: unknown): YesNo | undefined =>
  v === 'yes' || v === 'no' ? v : undefined;

const asYesNoUnknown = (v: unknown): YesNoUnknown | undefined =>
  v === 'yes' || v === 'no' || v === 'unknown' ? v : undefined;

/** Câmpurile obligatorii din `assertRequiredSelections` (secțiunile II–VI + GDPR). */
const REQUIRED_YES_NO: Array<{ section: keyof AnamnesisValues; key: string }> = [
  { section: 'medical', key: 'previousSkinCancer' },
  { section: 'medical', key: 'previousOtherCancer' },
  { section: 'medical', key: 'geneticMutation' },
  { section: 'medical', key: 'chronicDermatologicConditions' },
  { section: 'medical', key: 'autoimmuneOrImmunosuppression' },
  { section: 'medical', key: 'photosensitizingMedication' },
  { section: 'family', key: 'familySkinCancer' },
  { section: 'family', key: 'familyGeneticDisease' },
  { section: 'uv', key: 'severeSunburns' },
  { section: 'uv', key: 'dailySunscreen' },
  { section: 'uv', key: 'tanningWithoutSpf' },
  { section: 'uv', key: 'tanningSalonUse' },
  { section: 'uv', key: 'highUvResidence' },
  { section: 'uv', key: 'toxicChemicalExposure' },
  { section: 'skin', key: 'recentSkinChanges' },
  { section: 'skin', key: 'nonHealingLesions' },
  { section: 'skin', key: 'painOrSensitivity' },
  { section: 'lifestyle', key: 'smoking' },
  { section: 'lifestyle', key: 'alcoholUse' },
  { section: 'lifestyle', key: 'balancedDiet' },
  { section: 'lifestyle', key: 'regularDermatologyChecks' },
  { section: 'lifestyle', key: 'naturalSkinCareProducts' },
];

const SECTION_META: Array<{ key: SectionKey; title: string; required: number }> = [
  { key: 'medical', title: 'II. Istoric Medical Personal', required: 6 },
  { key: 'family', title: 'III. Istoric Medical Familial', required: 2 },
  { key: 'uv', title: 'IV. Expunere UV și Factori de Risc', required: 8 },
  { key: 'skin', title: 'V. Evaluarea Pielii și Simptome', required: 4 },
  { key: 'lifestyle', title: 'VI. Stil de Viață și Obiceiuri', required: 5 },
  { key: 'notes', title: 'VII. Observații Suplimentare', required: 0 },
  { key: 'consent', title: 'VIII. Consimțământ GDPR', required: 1 },
];

const countCompleted = (values: AnamnesisValues, section: SectionKey): number => {
  if (section === 'notes') return values.notes.trim() ? 1 : 0;
  if (section === 'consent') return values.consent.personalDataProcessing ? 1 : 0;
  if (section === 'uv') {
    // sunExposureDaily + skinType + cele 6 Da/Nu din secțiune
    let n = REQUIRED_YES_NO.filter((f) => f.section === 'uv').filter(
      (f) => asYesNo(values.uv[f.key]) !== undefined,
    ).length;
    if (values.uv.sunExposureDaily) n += 1;
    if (values.skinType) n += 1;
    return n;
  }
  return REQUIRED_YES_NO.filter((f) => f.section === section).filter(
    (f) => asYesNo((values as Record<string, any>)[f.section]?.[f.key]) !== undefined
      || asYesNoUnknown((values as Record<string, any>)[f.section]?.[f.key]) !== undefined,
  ).length;
};

const Step2Anamnesis = () => {
  const { form, saveForm, loading } = useDermatoWizard();
  const [values, setValues] = useState<AnamnesisValues>(emptyValues());
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  /** După un save reușit, următoarea sincronizare din `form` nu trebuie să rescrie valorile. */
  const skipNextFormSync = useRef(false);

  useEffect(() => {
    if (!form) return;
    if (skipNextFormSync.current) {
      skipNextFormSync.current = false;
      return;
    }
    // Draft local (localStorage) — editări nesalvate supraviețuiesc la refresh.
    const draft = form.id ? readWizardDraft<AnamnesisValues>(form.id, 'anamnesis') : null;
    if (draft) {
      setValues(draft);
      setDirty(true);
      return;
    }
    const medical = { ...asRecord(form.medicalHistoryDetails) };
    const family = asRecord(form.familyHistoryDetails);
    const uv = asRecord(form.uvRiskFactors);
    const skin = asRecord(form.skinEvaluation);
    const lifestyle = asRecord(form.lifestyle);
    const consent = asRecord(form.consent);
    // Compat: drafturile vechi țineau observațiile în personalHistory.
    const notes = asString(form.notes) || asString(form.personalHistory);
    // Compat: tratamentele pot exista doar pe form.previousTreatments (string[]).
    const legacyTreatments = asStringArray(form.previousTreatments).join(', ');
    if (!asString(medical.previousSkinCancerTreatments) && legacyTreatments) {
      medical.previousSkinCancerTreatments = legacyTreatments;
    }
    // Compat: datele vechi țineau tratamentele ca text liber, fără tipuri bifate.
    if (!asStringArray(medical.previousSkinCancerTreatmentTypes).length) {
      const legacyTreatmentText = asString(medical.previousSkinCancerTreatments).trim();
      if (legacyTreatmentText) {
        medical.previousSkinCancerTreatmentTypes = [OTHER_TREATMENT_OPTION_VALUE];
        medical.previousSkinCancerTreatmentsOther = legacyTreatmentText;
      }
    }
    // Compat: tipul altui cancer era text liber; dacă există text și nu e bifat „alt tip”.
    if (!asStringArray(medical.previousOtherCancerTypes).length) {
      const legacyOtherType = asString(medical.previousOtherCancerType).trim();
      if (legacyOtherType) {
        medical.previousOtherCancerTypes = [OTHER_CANCER_OPTION_VALUE];
      }
    }
    // Compat: liste de opțiuni salvate anterior ca text liber → „Altul” + text.
    const migrateOtherList = (
      rec: Record<string, unknown>,
      typesKey: string,
      otherKey: string,
    ) => {
      if (asStringArray(rec[typesKey]).length) return;
      const text = asString(rec[otherKey]).trim();
      if (text) rec[typesKey] = [OTHER_OPTION_VALUE];
    };
    migrateOtherList(medical, 'geneticMutationTypes', 'geneticMutationType');
    migrateOtherList(medical, 'chronicDermatologicConditionTypes', 'chronicDermatologicConditionDetails');
    migrateOtherList(medical, 'photosensitizingMedicationTypes', 'photosensitizingMedicationDetails');
    migrateOtherList(uv, 'tanningWithoutSpfTypes', 'tanningWithoutSpfDetails');
    migrateOtherList(uv, 'toxicChemicalExposureTypes', 'toxicChemicalExposureDetails');
    migrateOtherList(uv, 'highUvResidenceTypes', 'highUvResidenceDetails');
    // Compat: alegeri simple salvate ca text liber → „Altul” + text.
    const migrateChoiceOther = (
      rec: Record<string, unknown>,
      choiceKey: string,
      otherKey: string,
    ) => {
      if (asString(rec[choiceKey]).trim()) return;
      const text = asString(rec[otherKey]).trim();
      if (text) rec[choiceKey] = OTHER_OPTION_VALUE;
    };
    migrateChoiceOther(uv, 'severeSunburnEpisode', 'severeSunburnEpisodes');
    migrateChoiceOther(uv, 'sunscreenSpfChoice', 'sunscreenSpf');
    migrateChoiceOther(uv, 'tanningSalonFrequencyChoice', 'tanningSalonFrequency');
    setValues({
      medical: { ...medical },
      family: { ...family },
      uv: { ...uv },
      skin: { ...skin },
      lifestyle: { ...lifestyle },
      skinType: form.skinType || asString(uv.skinType) || undefined,
      notes,
      consent: {
        personalDataProcessing: Boolean(consent.personalDataProcessing),
        registryInclusion: Boolean(consent.registryInclusion),
        futureContact: Boolean(consent.futureContact),
      },
    });
    setDirty(false);
  }, [form]);

  // Autosave local (debounce) cât timp există modificări nesalvate.
  useEffect(() => {
    if (!dirty || !form?.id) return;
    const timer = window.setTimeout(() => {
      writeWizardDraft(form.id, 'anamnesis', values);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [values, dirty, form?.id]);

  const setSection = useCallback(
    (section: 'medical' | 'family' | 'uv' | 'skin' | 'lifestyle', key: string, value: unknown) => {
      setValues((prev) => ({
        ...prev,
        [section]: { ...prev[section], [key]: value },
      }));
      setDirty(true);
    },
    [],
  );

  const setDetail = useCallback(
    (section: 'medical' | 'family' | 'uv' | 'skin' | 'lifestyle', key: string, value: string) => {
      setValues((prev) => ({
        ...prev,
        [section]: { ...prev[section], [key]: value },
      }));
      setDirty(true);
    },
    [],
  );

  /** Golește mai multe chei dintr-o secțiune (când părintele trece pe Nu). */
  const clearKeys = useCallback(
    (section: 'medical' | 'family' | 'uv' | 'skin' | 'lifestyle', keys: Array<[string, unknown]>) => {
      setValues((prev) => {
        const next = { ...prev[section] };
        for (const [key, empty] of keys) next[key] = empty;
        return { ...prev, [section]: next };
      });
      setDirty(true);
    },
    [],
  );

  const yn = (section: 'medical' | 'family' | 'uv' | 'skin' | 'lifestyle', key: string): YesNo | undefined =>
    asYesNo(values[section][key]);

  const detail = (section: 'medical' | 'family' | 'uv' | 'skin' | 'lifestyle', key: string): string =>
    asString(values[section][key]);

  const progress = useMemo(
    () => SECTION_META.map((s) => ({ ...s, done: countCompleted(values, s.key) })),
    [values],
  );

  // Total alegeri obligatorii din II–VI (fără GDPR / observații):
  // 22 Da/Nu + sunExposureDaily + skinType + selfExamFrequency = 25
  const requiredTotal = REQUIRED_YES_NO.length + 3;

  const completedRequired = useMemo(() => {
    let done = 0;
    for (const f of REQUIRED_YES_NO) {
      const v = (values as Record<string, any>)[f.section]?.[f.key];
      if (asYesNo(v) !== undefined || asYesNoUnknown(v) !== undefined) done += 1;
    }
    if (values.uv.sunExposureDaily) done += 1;
    if (values.skinType) done += 1;
    if (values.skin.selfExamFrequency) done += 1;
    return done;
  }, [values]);

  const persist = async (): Promise<boolean> => {
    if (!form) return true;
    if (!dirty) return true;
    try {
      setSaving(true);
      const medical = { ...asRecord(form.medicalHistoryDetails), ...values.medical };
      const family = { ...asRecord(form.familyHistoryDetails), ...values.family };
      const uv = { ...asRecord(form.uvRiskFactors), ...values.uv };
      const skin = { ...asRecord(form.skinEvaluation), ...values.skin };
      const lifestyle = { ...asRecord(form.lifestyle), ...values.lifestyle };

      const treatmentTypes = asStringArray(medical.previousSkinCancerTreatmentTypes);
      const treatmentOther = asString(medical.previousSkinCancerTreatmentsOther).trim();
      const treatmentLabels = SKIN_CANCER_TREATMENT_OPTIONS.filter(
        (o) => treatmentTypes.includes(o.value) && o.value !== OTHER_TREATMENT_OPTION_VALUE,
      ).map((o) => o.label);
      const composedTreatments = [...treatmentLabels, treatmentOther].filter(Boolean).join(', ');
      // `previousSkinCancerTreatments` păstrează doar textul „care…” (exportul compune lista).
      medical.previousSkinCancerTreatments = treatmentOther;
      const previousTreatments = composedTreatments
        ? [composedTreatments]
        : asYesNo(medical.previousSkinCancer) === 'no'
          ? []
          : asStringArray(form.previousTreatments);

      const saved = await saveForm({
        medicalHistoryDetails: medical,
        familyHistoryDetails: family,
        uvRiskFactors: uv,
        skinEvaluation: skin,
        lifestyle,
        skinType: (values.skinType as any) ?? form.skinType,
        previousTreatments,
        notes: values.notes,
        consent: {
          ...asRecord(form.consent),
          personalDataProcessing: values.consent.personalDataProcessing,
          registryInclusion: values.consent.registryInclusion,
          futureContact: values.consent.futureContact,
        },
      });
      if (!saved) return false;
      clearWizardDraft(form.id, 'anamnesis');
      skipNextFormSync.current = true;
      setDirty(false);
      return true;
    } finally {
      setSaving(false);
    }
  };

  useRegisterStepSave(persist);

  if (loading || !form) {
    return <Card loading title="Anamneză / Risc" />;
  }

  return (
    <Card
      title={<Space>🩺 Anamneză — Chestionar extins cancer de piele (II–VIII)</Space>}
      data-testid="dermato-anamnesis"
    >
      <Paragraph type="secondary">
        Completați conform chestionarului de anamneză. Întrebările de detaliu și câmpurile libere
        („Dacă da, specificați…”, „care…”) se afișează doar la răspunsul <strong>Da</strong> /
        când opțiunea corespunzătoare este bifată. Secțiunea I (identificare) rămâne în pasul 1.
      </Paragraph>

      <Alert
        type={completedRequired >= requiredTotal ? 'success' : 'info'}
        showIcon
        message={`Progres: ${completedRequired} / ${requiredTotal} răspunsuri obligatorii (II–VI)`}
        description={
          <span data-testid="anamnesis-progress-sections">
            {progress
              .filter((s) => s.required > 0 || s.key === 'notes')
              .map((s) => `${s.title.split('.')[0]}: ${s.done}${s.required ? `/${s.required}` : ''}`)
              .join(' · ')}
          </span>
        }
        style={{ marginBottom: 16 }}
        data-testid="anamnesis-progress"
      />

      <Form layout="vertical" className="anamnesis-fields">
        {/* ── II. Istoric Medical Personal ─────────────────────────────── */}
        <section
          id="anamnesis-sec-ii"
          data-spy-section="II. Istoric personal"
          className="anamnesis-section"
          data-testid="anamnesis-section-II"
        >
          <Divider orientation="left" plain>II. Istoric Medical Personal</Divider>

          <YesNoField
            label="Ați fost diagnosticat(ă) anterior cu cancer de piele?"
            value={yn('medical', 'previousSkinCancer')}
            onChange={(v) => {
              setSection('medical', 'previousSkinCancer', v);
              if (v !== 'yes') {
                clearKeys('medical', [
                  ['previousSkinCancerTypes', []],
                  ['previousSkinCancerOther', ''],
                  ['previousSkinCancerTreatmentTypes', []],
                  ['previousSkinCancerTreatmentsOther', ''],
                  ['previousSkinCancerTreatments', ''],
                ]);
              }
            }}
          />
          {/* DA → tip (fără text liber separat) + tratamente; NU → ambele ascunse */}
          {yn('medical', 'previousSkinCancer') === 'yes' && (
            <>
              <MultiCheckWithOtherField
                label="Tip cancer de piele diagnosticat"
                value={asStringArray(values.medical.previousSkinCancerTypes)}
                options={PREVIOUS_SKIN_CANCER_TYPE_OPTIONS}
                otherOptionValue={OTHER_OPTION_VALUE}
                otherLabel="Care altul"
                otherValue={detail('medical', 'previousSkinCancerOther')}
                otherPlaceholder="ex: alt tip de cancer de piele…"
                onChange={(v) => setSection('medical', 'previousSkinCancerTypes', v)}
                onOtherChange={(v) => setDetail('medical', 'previousSkinCancerOther', v)}
              />

              <MultiCheckWithOtherField
                label="Dacă ați avut cancer de piele, ce tratamente ați urmat?"
                value={asStringArray(values.medical.previousSkinCancerTreatmentTypes)}
                options={SKIN_CANCER_TREATMENT_OPTIONS}
                otherOptionValue={OTHER_TREATMENT_OPTION_VALUE}
                otherLabel="Care"
                otherValue={detail('medical', 'previousSkinCancerTreatmentsOther')}
                otherPlaceholder="ex: terapie fotodinamică, crioterapie…"
                onChange={(v) => setSection('medical', 'previousSkinCancerTreatmentTypes', v)}
                onOtherChange={(v) => setDetail('medical', 'previousSkinCancerTreatmentsOther', v)}
              />
            </>
          )}

          <YesNoField
            label="Ați fost diagnosticat(ă) anterior cu un alt tip de cancer?"
            value={yn('medical', 'previousOtherCancer')}
            onChange={(v) => {
              setSection('medical', 'previousOtherCancer', v);
              if (v !== 'yes') {
                clearKeys('medical', [
                  ['previousOtherCancerTypes', []],
                  ['previousOtherCancerType', ''],
                  ['previousOtherCancerTreatments', ''],
                ]);
              }
            }}
          />
          {/* NU → ascunde tipul + tratamentele; DA → listă Sân/Prostată/Pancreas/Colon/Altul */}
          {yn('medical', 'previousOtherCancer') === 'yes' && (
            <>
              <MultiCheckWithOtherField
                label="Dacă da, specificați tipul"
                value={asStringArray(values.medical.previousOtherCancerTypes)}
                options={OTHER_CANCER_TYPE_OPTIONS}
                otherOptionValue={OTHER_CANCER_OPTION_VALUE}
                otherLabel="Care altul"
                otherValue={detail('medical', 'previousOtherCancerType')}
                otherPlaceholder="ex: tiroidă, vezică, hematologic…"
                onChange={(v) => setSection('medical', 'previousOtherCancerTypes', v)}
                onOtherChange={(v) => setDetail('medical', 'previousOtherCancerType', v)}
              />

              <DetailTextInput
                label="Ce tratamente ați urmat pentru celălalt cancer?"
                value={detail('medical', 'previousOtherCancerTreatments')}
                placeholder="ex: chirurgie, chimioterapie, radioterapie, terapii țintite…"
                onChange={(v) => setDetail('medical', 'previousOtherCancerTreatments', v)}
              />
            </>
          )}

          <YesNoOptionsWithOtherField
            label="Ați fost diagnosticat(ă) cu o mutație genetică predispozantă la cancer de piele?"
            value={yn('medical', 'geneticMutation')}
            options={GENETIC_MUTATION_OPTIONS}
            selected={asStringArray(values.medical.geneticMutationTypes)}
            otherValue={detail('medical', 'geneticMutationType')}
            otherLabel="Dacă altul, specificați care mutație"
            otherPlaceholder="ex: PTEN, TP53, Xeroderma pigmentosum…"
            onChange={(v) => setSection('medical', 'geneticMutation', v)}
            onOptionsChange={(v) => setSection('medical', 'geneticMutationTypes', v)}
            onOtherChange={(v) => setDetail('medical', 'geneticMutationType', v)}
          />

          <YesNoOptionsWithOtherField
            label="Aveți alte afecțiuni dermatologice cronice?"
            value={yn('medical', 'chronicDermatologicConditions')}
            options={CHRONIC_SKIN_CONDITION_OPTIONS}
            selected={asStringArray(values.medical.chronicDermatologicConditionTypes)}
            otherValue={detail('medical', 'chronicDermatologicConditionDetails')}
            otherLabel="Dacă altul, specificați care"
            otherPlaceholder="ex: lupus cutanat, lichen plan, keratoză actinică…"
            onChange={(v) => setSection('medical', 'chronicDermatologicConditions', v)}
            onOptionsChange={(v) => setSection('medical', 'chronicDermatologicConditionTypes', v)}
            onOtherChange={(v) => setDetail('medical', 'chronicDermatologicConditionDetails', v)}
          />

          <YesNoField
            label="Aveți boli autoimune sau un sistem imunitar slăbit?"
            value={yn('medical', 'autoimmuneOrImmunosuppression')}
            onChange={(v) => setSection('medical', 'autoimmuneOrImmunosuppression', v)}
          />

          <YesNoOptionsWithOtherField
            label="Luați medicamente care cresc sensibilitatea pielii la soare?"
            value={yn('medical', 'photosensitizingMedication')}
            options={PHOTOSENSITIZING_MEDICATION_OPTIONS}
            selected={asStringArray(values.medical.photosensitizingMedicationTypes)}
            otherValue={detail('medical', 'photosensitizingMedicationDetails')}
            otherLabel="Dacă altul, specificați care"
            otherPlaceholder="ex: amiodaronă, sulfonamide, ciclosporină…"
            onChange={(v) => setSection('medical', 'photosensitizingMedication', v)}
            onOptionsChange={(v) => setSection('medical', 'photosensitizingMedicationTypes', v)}
            onOtherChange={(v) => setDetail('medical', 'photosensitizingMedicationDetails', v)}
          />
        </section>

        {/* ── III. Istoric Medical Familial ────────────────────────────── */}
        <section
          id="anamnesis-sec-iii"
          data-spy-section="III. Istoric familial"
          className="anamnesis-section"
          data-testid="anamnesis-section-III"
        >
          <Divider orientation="left" plain>III. Istoric Medical Familial</Divider>

          <YesNoField
            label="Există cazuri de cancer de piele în familie?"
            value={yn('family', 'familySkinCancer')}
            onChange={(v) => {
              setSection('family', 'familySkinCancer', v);
              if (v !== 'yes') {
                clearKeys('family', [
                  ['familySkinCancerTypes', []],
                  ['familySkinCancerTypeOther', ''],
                  ['familySkinCancerDetails', ''],
                ]);
              }
            }}
          />
          {yn('family', 'familySkinCancer') === 'yes' && (
            <>
              <MultiCheckWithOtherField
                label="Ce tip de cancer de piele?"
                value={asStringArray(values.family.familySkinCancerTypes)}
                options={PREVIOUS_SKIN_CANCER_TYPE_OPTIONS}
                otherOptionValue={OTHER_OPTION_VALUE}
                otherLabel="Dacă altul, specificați care"
                otherValue={detail('family', 'familySkinCancerTypeOther')}
                otherPlaceholder="ex: alt tip de cancer de piele…"
                onChange={(v) => setSection('family', 'familySkinCancerTypes', v)}
                onOtherChange={(v) => setDetail('family', 'familySkinCancerTypeOther', v)}
              />
              <DetailTextInput
                label="Dacă da, specificați rudele"
                value={detail('family', 'familySkinCancerDetails')}
                placeholder="ex: mamă, unchi matern, bunică…"
                onChange={(v) => setDetail('family', 'familySkinCancerDetails', v)}
              />
            </>
          )}

          <YesNoUnknownField
            label="Există alte afecțiuni genetice asociate cancerului de piele în familie?"
            value={asYesNoUnknown(values.family.familyGeneticDisease)}
            onChange={(v) => {
              setSection('family', 'familyGeneticDisease', v);
              if (v !== 'yes') setSection('family', 'familyGeneticDiseaseDetails', '');
            }}
          />
          {asYesNoUnknown(values.family.familyGeneticDisease) === 'yes' && (
            <Form.Item label="Dacă da, specificați rudele și tipul de afecțiuni" style={{ marginLeft: 12 }}>
              <Input.TextArea
                rows={2}
                value={detail('family', 'familyGeneticDiseaseDetails')}
                onChange={(e) => setDetail('family', 'familyGeneticDiseaseDetails', e.target.value)}
              />
            </Form.Item>
          )}
        </section>

        {/* ── IV. Expunere UV ──────────────────────────────────────────── */}
        <section
          id="anamnesis-sec-iv"
          data-spy-section="IV. Expunere UV"
          className="anamnesis-section"
          data-testid="anamnesis-section-IV"
        >
          <Divider orientation="left" plain>IV. Expunere la Radiații UV și Factori de Risc</Divider>
          <SectionHint>Completați toate câmpurile — alimentează scorul de risc și exportul PDF.</SectionHint>

          <ChoiceField
            label="Cât timp petreceți zilnic la soare?"
            required
            value={asString(values.uv.sunExposureDaily) || undefined}
            options={SUN_EXPOSURE_OPTIONS}
            onChange={(v) => setSection('uv', 'sunExposureDaily', v)}
          />

          <YesNoChoiceWithOtherField
            label="Ați suferit arsuri solare severe cu bășici?"
            value={yn('uv', 'severeSunburns')}
            choiceValue={asString(values.uv.severeSunburnEpisode) || undefined}
            options={SUNBURN_EPISODE_OPTIONS}
            choiceLabel="Dacă da, câte episoade?"
            otherValue={detail('uv', 'severeSunburnEpisodes')}
            otherLabel="Dacă altul, specificați câte"
            otherPlaceholder="ex: 7, 10, „multe”…"
            onChange={(v) => {
              setSection('uv', 'severeSunburns', v);
              if (v !== 'yes') clearKeys('uv', [['severeSunburnEpisode', ''], ['severeSunburnEpisodes', '']]);
            }}
            onChoiceChange={(v) => setSection('uv', 'severeSunburnEpisode', v)}
            onOtherChange={(v) => setDetail('uv', 'severeSunburnEpisodes', v)}
          />

          <YesNoChoiceWithOtherField
            label="Utilizați protecție solară zilnic?"
            value={yn('uv', 'dailySunscreen')}
            choiceValue={asString(values.uv.sunscreenSpfChoice) || undefined}
            options={SPF_OPTIONS}
            choiceLabel="Dacă da, ce factor SPF?"
            otherValue={detail('uv', 'sunscreenSpf')}
            otherLabel="Dacă altul, specificați care"
            otherPlaceholder="ex: SPF 20, SPF 60, mineral…"
            onChange={(v) => {
              setSection('uv', 'dailySunscreen', v);
              if (v !== 'yes') clearKeys('uv', [['sunscreenSpfChoice', ''], ['sunscreenSpf', '']]);
            }}
            onChoiceChange={(v) => setSection('uv', 'sunscreenSpfChoice', v)}
            onOtherChange={(v) => setDetail('uv', 'sunscreenSpf', v)}
          />

          <YesNoOptionsWithOtherField
            label="Utilizați uleiuri/creme fără SPF pentru bronzare rapidă?"
            value={yn('uv', 'tanningWithoutSpf')}
            options={TANNING_PRODUCT_OPTIONS}
            selected={asStringArray(values.uv.tanningWithoutSpfTypes)}
            otherValue={detail('uv', 'tanningWithoutSpfDetails')}
            otherLabel="Dacă altul, specificați care"
            otherPlaceholder="ex: spray bronzant, pudră bronzantă…"
            onChange={(v) => setSection('uv', 'tanningWithoutSpf', v)}
            onOptionsChange={(v) => setSection('uv', 'tanningWithoutSpfTypes', v)}
            onOtherChange={(v) => setDetail('uv', 'tanningWithoutSpfDetails', v)}
          />

          <YesNoChoiceWithOtherField
            label="Frecventați saloane de bronzare artificială?"
            value={yn('uv', 'tanningSalonUse')}
            choiceValue={asString(values.uv.tanningSalonFrequencyChoice) || undefined}
            options={TANNING_SALON_FREQUENCY_OPTIONS}
            choiceLabel="Dacă da, cât de des?"
            otherValue={detail('uv', 'tanningSalonFrequency')}
            otherLabel="Dacă altul, specificați cât de des"
            otherPlaceholder="ex: de 2 ori pe lună, doar vara…"
            onChange={(v) => {
              setSection('uv', 'tanningSalonUse', v);
              if (v !== 'yes') clearKeys('uv', [['tanningSalonFrequencyChoice', ''], ['tanningSalonFrequency', '']]);
            }}
            onChoiceChange={(v) => setSection('uv', 'tanningSalonFrequencyChoice', v)}
            onOtherChange={(v) => setDetail('uv', 'tanningSalonFrequency', v)}
          />

          <YesNoOptionsWithOtherField
            label="Ați locuit într-o zonă cu radiații solare intense (tropice, munți)?"
            value={yn('uv', 'highUvResidence')}
            options={HIGH_UV_ZONE_OPTIONS}
            selected={asStringArray(values.uv.highUvResidenceTypes)}
            otherValue={detail('uv', 'highUvResidenceDetails')}
            otherLabel="Dacă altul, specificați zona"
            otherPlaceholder="ex: deșert, altitudine mare…"
            onChange={(v) => {
              setSection('uv', 'highUvResidence', v);
              if (v !== 'yes') clearKeys('uv', [['highUvResidenceTypes', []], ['highUvResidenceDetails', '']]);
            }}
            onOptionsChange={(v) => setSection('uv', 'highUvResidenceTypes', v)}
            onOtherChange={(v) => setDetail('uv', 'highUvResidenceDetails', v)}
          />

          <ChoiceField
            label="Care este tipul dvs. de piele în funcție de reacția la soare? (Fitzpatrick)"
            required
            value={values.skinType as any}
            options={SKIN_TYPE_OPTIONS as any}
            onChange={(v) => {
              setValues((prev) => ({ ...prev, skinType: v }));
              setDirty(true);
            }}
          />

          <YesNoOptionsWithOtherField
            label="Ați fost expus la substanțe chimice toxice (arsenic, hidrocarburi, gudron…)?"
            value={yn('uv', 'toxicChemicalExposure')}
            options={TOXIC_CHEMICAL_OPTIONS}
            selected={asStringArray(values.uv.toxicChemicalExposureTypes)}
            otherValue={detail('uv', 'toxicChemicalExposureDetails')}
            otherLabel="Dacă altul, specificați care"
            otherPlaceholder="ex: solvenți, pesticide, formaldehidă…"
            onChange={(v) => setSection('uv', 'toxicChemicalExposure', v)}
            onOptionsChange={(v) => setSection('uv', 'toxicChemicalExposureTypes', v)}
            onOtherChange={(v) => setDetail('uv', 'toxicChemicalExposureDetails', v)}
          />
        </section>

        {/* ── V. Evaluarea Pielii ──────────────────────────────────────── */}
        <section
          id="anamnesis-sec-v"
          data-spy-section="V. Evaluarea pielii"
          className="anamnesis-section"
          data-testid="anamnesis-section-V"
        >
          <Divider orientation="left" plain>V. Evaluarea Pielii și Simptome</Divider>

          <YesNoField
            label="Ați observat modificări ale pielii în ultimele 6 luni?"
            value={yn('skin', 'recentSkinChanges')}
            onChange={(v) => {
              setSection('skin', 'recentSkinChanges', v);
              if (v !== 'yes') {
                clearKeys('skin', [
                  ['skinChanges', []],
                  ['skinChangesOther', ''],
                ]);
              }
            }}
          />
          {yn('skin', 'recentSkinChanges') === 'yes' && (
            <MultiCheckWithOtherField
              label="Ce tip de modificări?"
              value={asStringArray(values.skin.skinChanges)}
              options={SKIN_CHANGES_WITH_OTHER_OPTIONS}
              otherOptionValue={OTHER_OPTION_VALUE}
              otherLabel="Dacă altul, specificați care"
              otherValue={detail('skin', 'skinChangesOther')}
              otherPlaceholder="ex: cruste, umflături, modificări de textură…"
              onChange={(v) => setSection('skin', 'skinChanges', v)}
              onOtherChange={(v) => setDetail('skin', 'skinChangesOther', v)}
            />
          )}

          <YesNoField
            label="Ați observat pete roșiatice, solzoase sau leziuni care nu se vindecă?"
            value={yn('skin', 'nonHealingLesions')}
            onChange={(v) => setSection('skin', 'nonHealingLesions', v)}
          />

          <YesNoField
            label="Aveți durere sau sensibilitate crescută în zonele afectate?"
            value={yn('skin', 'painOrSensitivity')}
            onChange={(v) => setSection('skin', 'painOrSensitivity', v)}
          />

          <ChoiceField
            label="Cât de des vă examinați pielea?"
            required
            value={asString(values.skin.selfExamFrequency) || undefined}
            options={SELF_EXAM_OPTIONS}
            onChange={(v) => setSection('skin', 'selfExamFrequency', v)}
          />
        </section>

        {/* ── VI. Stil de Viață ────────────────────────────────────────── */}
        <section
          id="anamnesis-sec-vi"
          data-spy-section="VI. Stil de viață"
          className="anamnesis-section"
          data-testid="anamnesis-section-VI"
        >
          <Divider orientation="left" plain>VI. Stil de Viață și Obiceiuri</Divider>

          <YesNoDetailField
            label="Fumați?"
            value={yn('lifestyle', 'smoking')}
            detailLabel="Dacă da, de câți ani?"
            detailValue={detail('lifestyle', 'smokingYears')}
            onChange={(v) => setSection('lifestyle', 'smoking', v)}
            onDetailChange={(v) => setDetail('lifestyle', 'smokingYears', v)}
          />

          <YesNoDetailField
            label="Consumați alcool?"
            value={yn('lifestyle', 'alcoholUse')}
            detailLabel="Dacă da, cât de frecvent?"
            detailValue={detail('lifestyle', 'alcoholFrequency')}
            onChange={(v) => setSection('lifestyle', 'alcoholUse', v)}
            onDetailChange={(v) => setDetail('lifestyle', 'alcoholFrequency', v)}
          />

          <YesNoField
            label="Aveți o dietă echilibrată, bogată în vitamine și antioxidanți?"
            value={yn('lifestyle', 'balancedDiet')}
            onChange={(v) => setSection('lifestyle', 'balancedDiet', v)}
          />

          <YesNoDetailField
            label="Mergeți la controale dermatologice regulate?"
            value={yn('lifestyle', 'regularDermatologyChecks')}
            detailLabel="Dacă da, ultima dată"
            detailValue={detail('lifestyle', 'lastDermatologyCheckDate')}
            onChange={(v) => setSection('lifestyle', 'regularDermatologyChecks', v)}
            onDetailChange={(v) => setDetail('lifestyle', 'lastDermatologyCheckDate', v)}
          />

          <YesNoField
            label="Utilizați produse de îngrijire a pielii cu ingrediente naturale?"
            value={yn('lifestyle', 'naturalSkinCareProducts')}
            onChange={(v) => setSection('lifestyle', 'naturalSkinCareProducts', v)}
          />
        </section>

        {/* ── VII. Observații ──────────────────────────────────────────── */}
        <section
          id="anamnesis-sec-vii"
          data-spy-section="VII. Observații"
          className="anamnesis-section"
          data-testid="anamnesis-section-VII"
        >
          <Divider orientation="left" plain>VII. Observații Suplimentare</Divider>
          <SectionHint>Menționați orice alte simptome sau probleme legate de piele.</SectionHint>
          <Form.Item label="Observații">
            <textarea
              value={values.notes}
              onChange={(e) => {
                setValues((prev) => ({ ...prev, notes: e.target.value }));
                setDirty(true);
              }}
              rows={4}
              data-testid="anamnesis-notes"
              style={{
                width: '100%',
                padding: 8,
                border: '1px solid #d9d9d9',
                borderRadius: 6,
                fontFamily: 'inherit',
              }}
              placeholder="ex: pacientul relatează expunere profesională la arsenic în copilărie…"
            />
          </Form.Item>
        </section>

        {/* ── VIII. GDPR ───────────────────────────────────────────────── */}
        <section
          id="anamnesis-sec-viii"
          data-spy-section="VIII. GDPR"
          className="anamnesis-section anamnesis-consent"
          data-testid="anamnesis-section-VIII"
        >
          <Divider orientation="left" plain>VIII. Consimțământ GDPR</Divider>
          <Paragraph type="secondary" style={{ marginBottom: 12 }}>
            Prelucrare date în scop de screening/diagnostic cancer de piele și/sau hepatic (proiect HEPASKIN).
            Refuzul poate limita accesul la anumite servicii de screening și tratament.
          </Paragraph>
          <Form.Item
            required
            label="Acordul pacientului"
            validateStatus={values.consent.personalDataProcessing ? 'success' : undefined}
          >
            <Checkbox
              checked={values.consent.personalDataProcessing}
              onChange={(e) => {
                setValues((prev) => ({
                  ...prev,
                  consent: { ...prev.consent, personalDataProcessing: e.target.checked },
                }));
                setDirty(true);
              }}
            >
              Sunt de acord cu prelucrarea datelor mele personale în scopurile menționate mai sus.
            </Checkbox>
            <Checkbox
              checked={values.consent.registryInclusion}
              onChange={(e) => {
                setValues((prev) => ({
                  ...prev,
                  consent: { ...prev.consent, registryInclusion: e.target.checked },
                }));
                setDirty(true);
              }}
            >
              Îmi dau consimțământul pentru includerea datelor mele în registrul transfrontalier
              de cancer de piele și/sau cancer hepatic, în scopul monitorizării medicale.
            </Checkbox>
            <Checkbox
              checked={values.consent.futureContact}
              onChange={(e) => {
                setValues((prev) => ({
                  ...prev,
                  consent: { ...prev.consent, futureContact: e.target.checked },
                }));
                setDirty(true);
              }}
            >
              Sunt de acord să fiu contactat(ă) pentru urmărirea stării mele de sănătate și pentru
              programări medicale viitoare.
            </Checkbox>
          </Form.Item>
        </section>

        <Divider />

        <Form.Item>
          <Button
            type="primary"
            onClick={() => void persist()}
            loading={saving}
            disabled={!dirty}
            data-testid="anamnesis-save"
          >
            Salvează anamneză
          </Button>
          {dirty && (
            <Text type="secondary" style={{ marginLeft: 12 }}>
              Modificări nesalvate
            </Text>
          )}
        </Form.Item>
      </Form>
    </Card>
  );
};

export default Step2Anamnesis;
